import { isResource } from '@medplum/core'
import type {
  Communication,
  Composition,
  Organization,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  EDITED_DATE_SYSTEM,
  ELATION_NON_VISIT_NOTE_TYPE_SYSTEM,
  TWENTY_NOTE_ID_SYSTEM,
} from '#src/codeSystems.ts'
import type { DatetimeString } from '#src/dates.ts'
import { Extension } from '#src/decorators.ts'
import type { ExportFields } from '#src/ExtendedResource.ts'
import { COMMUNICATION_CATEGORIES } from '#src/models/communications/constants.ts'
import { ExtendedCommunication } from '#src/models/communications/ExtendedCommunication.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

const FAKE_UUID = '00000000-0000-0000-0000-000000000000'
const FAKE_PART_OF = [{ reference: `Communication/${FAKE_UUID}` }]

export class CommentCommunication extends ExtendedCommunication {
  // Note: This is named CommentCommunication instead of Comment because the Comment interface is already defined in the global namespace: https://developer.mozilla.org/en-US/docs/Web/API/Comment

  static searchQueryAll(): QueryTypes {
    return {
      ...ExtendedCommunication.baseSearchQuery(),
      category: COMMUNICATION_CATEGORIES.comment,
      _sort: '-sent',
    }
  }

  static searchQuery(partOf: Reference[], sender?: Reference): QueryTypes {
    return {
      ...CommentCommunication.searchQueryAll(),
      'part-of': partOf.length > 0 ? partOf : FAKE_PART_OF,
      ...(sender && { sender }),
    }
  }

  static searchByParticipant(participant: Reference<Patient>): QueryTypes {
    return {
      ...CommentCommunication.searchQueryAll(),
      subject: participant,
    }
  }

  static queryTimelineComments(participant: Reference<Patient>): QueryTypes {
    return {
      ...CommentCommunication.searchQueryAll(),
      'part-of': participant,
    }
  }

  static queryByDateRange(
    startDate: DatetimeString,
    endDate: DatetimeString
  ): QueryTypes {
    return {
      ...CommentCommunication.searchQueryAll(),
      _filter: `sent ge ${startDate} and sent le ${endDate}`,
    }
  }

  get parentComment(): Reference<Communication> | undefined {
    const maybeParentCommentReferences =
      this.partOf?.filter((ref) => isOfType(ref, 'Communication')) ?? []
    if (maybeParentCommentReferences.length !== 0) {
      if (maybeParentCommentReferences.length > 1) {
        console.warn('Multiple parent comments are not supported')
      }
      return maybeParentCommentReferences[0]
    }
    return undefined
  }

  get visitNote(): Reference<Composition> | undefined {
    return this.partOf?.find((ref) => isOfType(ref, 'Composition'))
  }

  constructor({
    partOf,
    message,
    sender,
    sent,
    elationCategory,
    subject,
    orderRevokeReason,
  }: {
    partOf: Reference[]
    message: string
    sender: Reference<Practitioner | Organization>
    sent?: string
    elationCategory?: string
    subject?: Reference<Patient>
    orderRevokeReason?: boolean
  }) {
    super({
      partOf,
      message,
      sender,
      category: {
        ...COMMUNICATION_CATEGORIES.comment,
        coding: compact([
          ...(COMMUNICATION_CATEGORIES.comment.coding ?? []),
          elationCategory && {
            system: ELATION_NON_VISIT_NOTE_TYPE_SYSTEM,
            code: elationCategory,
            display: elationCategory,
          },
          orderRevokeReason &&
            COMMUNICATION_CATEGORIES.orderRevokeReason.coding?.[0],
        ]),
      },
      sent,
    })
    const patient = subject ?? partOf.find((ref) => isOfType(ref, 'Patient'))
    if (patient) {
      this.subject = patient
    }
  }

  @Extension({
    url: EDITED_DATE_SYSTEM,
    kind: 'valueDateTime',
  })
  editedDate?: string

  get twentyNoteId(): string | undefined {
    return this.identifier(TWENTY_NOTE_ID_SYSTEM)?.value
  }

  set twentyNoteId(value: string | undefined) {
    if (value) {
      this.setIdentifier(TWENTY_NOTE_ID_SYSTEM, value)
    } else {
      this.removeIdentifier(TWENTY_NOTE_ID_SYSTEM)
    }
  }

  static byTwentyNoteId(noteId: string): QueryTypes {
    return {
      ...this.searchQueryAll(),
      identifier: `${TWENTY_NOTE_ID_SYSTEM}|${noteId}`,
    }
  }

  protected getExportFields(): ExportFields {
    return {
      ...super.getExportFields(),
      editedDate: this.editedDate,
      twentyNoteId: this.twentyNoteId,
    }
  }

  static isInstance(value: unknown): value is Communication {
    return (
      isResource(value) &&
      isOfType(value, 'Communication') &&
      hasCodeableConcept(value.category, COMMUNICATION_CATEGORIES.comment)
    )
  }
}
